// NPC snapshots have no player command/pose timeline. Retain their existing
// lightweight motion interpolation independently of player control protocol V2.
import { WaterLocomotion } from "@endclock/shared";
import { smoothFacing, boundedCharacterWetness } from "../character/character-animation.js";
const REMOTE_RUN_THRESHOLD = 5.15;
const REMOTE_INTERPOLATION_MINIMUM = 0.08;
const REMOTE_INTERPOLATION_MAXIMUM = 0.22;

export class NpcMotionInterpolator {
  x: number;
  y: number;
  z: number;
  yaw: number;
  speed = 0;
  running = false;
  locomotion: WaterLocomotion = WaterLocomotion.Ground;
  wetness = 0;
  private startX: number;
  private startY: number;
  private startZ: number;
  private targetX: number;
  private targetY: number;
  private targetZ: number;
  private targetYaw: number;
  private elapsed = 0;
  private duration = 0.1;
  private sinceSnapshot = 0;
  private lastServerAt = 0;
  private lastTick = -1;

  constructor(x: number, y: number, z: number, yaw: number) {
    this.x = x;
    this.y = y;
    this.z = z;
    this.yaw = yaw;
    this.startX = x;
    this.startY = y;
    this.startZ = z;
    this.targetX = x;
    this.targetY = y;
    this.targetZ = z;
    this.targetYaw = yaw;
  }

  ingest(x: number, y: number, z: number, yaw: number, serverAt: number, tick: number,
    locomotion: WaterLocomotion = WaterLocomotion.Ground, wetness = 0, velocityX?: number, velocityZ?: number): void {
    if (tick <= this.lastTick) return;
    const serverDelta = this.lastServerAt > 0 ? Math.max(0.025, Math.min(0.5, (serverAt - this.lastServerAt) / 1_000)) : 0.1;
    const distance = Math.hypot(x - this.targetX, z - this.targetZ);
    this.startX = this.x;
    this.startY = this.y;
    this.startZ = this.z;
    this.targetX = x;
    this.targetY = y;
    this.targetZ = z;
    this.targetYaw = yaw;
    this.elapsed = 0;
    this.sinceSnapshot = 0;
    this.duration = Math.max(
      REMOTE_INTERPOLATION_MINIMUM,
      Math.min(REMOTE_INTERPOLATION_MAXIMUM, serverDelta * 1.15),
    );
    this.speed = velocityX !== undefined && velocityZ !== undefined
      ? Math.hypot(velocityX, velocityZ) : distance < 0.006 ? 0 : distance / serverDelta;
    this.locomotion = locomotion;
    this.wetness = boundedCharacterWetness(wetness);
    this.running = locomotion === WaterLocomotion.Ground && this.speed >= REMOTE_RUN_THRESHOLD;
    this.lastServerAt = serverAt;
    this.lastTick = tick;
  }

  update(delta: number, snapshotAge?: number): void {
    const elapsed = Number.isFinite(delta) ? Math.max(0, delta) : 0;
    this.elapsed = Math.min(this.duration, this.elapsed + elapsed);
    const previousAge = this.sinceSnapshot;
    // A snapshot ingested this frame was received after the preceding frame gap.
    // Its freshness must use receive time, independently of visual catch-up.
    this.sinceSnapshot = snapshotAge !== undefined && Number.isFinite(snapshotAge)
      ? Math.max(0, snapshotAge) : previousAge + elapsed;
    const linear = this.duration > 0 ? this.elapsed / this.duration : 1;
    const amount = linear * linear * (3 - 2 * linear);
    this.x = this.startX + (this.targetX - this.startX) * amount;
    this.y = this.startY + (this.targetY - this.startY) * amount;
    this.z = this.startZ + (this.targetZ - this.startZ) * amount;
    this.yaw = smoothFacing(this.yaw, this.targetYaw, elapsed, 16);
    const staleElapsed = Math.max(0, this.sinceSnapshot - Math.max(previousAge, this.duration + 0.14));
    if (staleElapsed > 0) {
      this.speed *= Math.exp(-14 * staleElapsed);
      if (this.speed < 0.08) this.speed = 0;
      this.running = this.locomotion === WaterLocomotion.Ground && this.speed >= REMOTE_RUN_THRESHOLD;
    }
  }
}
